import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { CheckCircle2, Plus, UserPlus, Trash2, ChevronDown, GripHorizontal } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';

const calcHrs = (inT: string, outT: string): number => {
  if (!inT || !outT) return 0;
  const [ih, im] = inT.split(':').map(Number);
  const [oh, om] = outT.split(':').map(Number);
  let diff = oh + om / 60 - (ih + im / 60);
  if (diff <= 0) diff += 24;
  return Math.round(diff * 10) / 10;
};

const SESSION_COLORS = [
  { bg: '#dbeafe', bgFilled: '#eff6ff', text: '#1d4ed8', border: '#93c5fd', dot: '#2563eb', headerBg: '#bfdbfe' },
  { bg: '#fde8d0', bgFilled: '#fff7ed', text: '#c2410c', border: '#fdba74', dot: '#ea580c', headerBg: '#fed7aa' },
  { bg: '#dde3fe', bgFilled: '#eef2ff', text: '#4338ca', border: '#a5b4fc', dot: '#4f46e5', headerBg: '#c7d2fe' },
  { bg: '#d1fae5', bgFilled: '#ecfdf5', text: '#047857', border: '#6ee7b7', dot: '#059669', headerBg: '#a7f3d0' },
  { bg: '#ede9fe', bgFilled: '#faf5ff', text: '#6d28d9', border: '#c4b5fd', dot: '#7c3aed', headerBg: '#ddd6fe' },
  { bg: '#ffd7da', bgFilled: '#fff1f2', text: '#be123c', border: '#fda4af', dot: '#e11d48', headerBg: '#fecdd3' },
];

// The unset column. One constant so the grid and the dropdown can't disagree
// about what "no shift chosen yet" looks like.
const PLACEHOLDER = 'Select Shift...';

const NURSING_shiftOptions = [
  PLACEHOLDER,
  '08 Morning', '08 Afternoon', '08 Night',
  '12 Day', '12 Night', '24 Hrs',
  '08 Morning - Junior', '08 Afternoon - Junior', '08 Night - Junior',
  '12 Day - Junior', '12 Night - Junior', '24 Hrs Junior',
];

const PHYSIO_shiftOptions = [
  PLACEHOLDER,
  'Morning Visit', 'Afternoon Visit', 'Evening Visit',
];

// Visit-based duties (physiotherapy + nursing visits) use the Visit shift options.
const getShiftOptions = (dutyType?: string) => {
  const t = (dutyType || '').toLowerCase();
  return (t.includes('physio') || t.includes('visit')) ? PHYSIO_shiftOptions : NURSING_shiftOptions;
};

interface ShiftColumn { id: string; label: string; type: string; }
interface SelectedSlot { date: string; shiftId: string; session: string; }
interface SelectionPoint { dayIdx: number; shiftIdx: number; }
interface AssignmentData {
  nurse: string; type?: string; hasAssistant?: boolean; assistant?: string;
  is_no_duty?: boolean; is_overridden?: boolean;
  in_time?: string; out_time?: string; actual_hours?: number;
  rate?: number; total_charge?: number;
  nurse_id?: number | null; second_nurse_id?: number | null;
  second_rate?: number | null; second_total_charge?: number | null;
  second_in_time?: string | null; second_out_time?: string | null;
}
// A staffed cell covers the primary nurse and, when present, an assistant. The
// Shifts cell already counts the assistant (2), so Charge must include their
// money too — otherwise the column shows one person's charge while claiming two
// shifts, and the Total row disagrees with the cells above it. Both the cell and
// the Total go through these, so they can't drift apart again.
const cellHasAssistant = (d: AssignmentData): boolean => !!(d.hasAssistant && d.assistant);

const cellCharge = (d: AssignmentData): number =>
  (d.total_charge || 0) + (cellHasAssistant(d) ? (d.second_total_charge || 0) : 0);

interface ScheduleGridProps {
  patientId?: number; currentMonth: Date; dutyType?: string;
  shifts: ShiftColumn[]; setShifts: (shifts: ShiftColumn[]) => void;
  scheduleMap: Record<string, Record<string, AssignmentData>>;
  selectedSlots: SelectedSlot[]; setSelectedSlots: (slots: SelectedSlot[]) => void;
  isFetching: boolean;
  // Cells with a "change alert" flag → `${date}|${shift_type}`. Shown yellow so
  // the team sees a shift whose assignment was changed (or was flagged on the board).
  flaggedCells?: Set<string>;
  /** Deleting a column clears rows on the server; the owner reloads the month. */
  onAssignmentsDeleted?: () => void;
}

// Fixed header/footer heights (px) — must match the rendered th heights below
const H1 = 32; // header row 1
const H2 = 22; // header row 2
const HF = 24; // footer row
const FIXED_H = H1 + H2 + HF; // 78 px

const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  patientId, currentMonth, dutyType, shifts, setShifts, scheduleMap, selectedSlots, setSelectedSlots, isFetching, flaggedCells,
  onAssignmentsDeleted,
}) => {
  const gridRef   = useRef<HTMLDivElement>(null);
  const mouseYRef = useRef<number>(0);
  const rafRef    = useRef<number | null>(null);

  const [isSelecting, setIsSelecting]         = useState(false);
  const [selectionStart, setSelectionStart]   = useState<SelectionPoint | null>(null);
  const [selectionEnd,   setSelectionEnd]     = useState<SelectionPoint | null>(null);
  const [draggedColIdx,  setDraggedColIdx]    = useState<number | null>(null);
  // A column awaiting the "this will delete its assignments" confirmation.
  const [pendingColumnDelete, setPendingColumnDelete] = useState<ShiftColumn | null>(null);
  const [containerWidth,  setContainerWidth]  = useState(() => window.innerWidth);
  const [containerHeight, setContainerHeight] = useState(() => window.innerHeight * 0.8);
  // One tooltip for both staff roles — `role` picks the accent + footer label.
  const [staffTooltip, setStaffTooltip] = useState<{
    role: 'primary' | 'assistant';
    name: string; shiftType: string;
    inTime?: string; outTime?: string; hours?: number; charge?: number;
    x: number; y: number;
  } | null>(null);

  // Track both dimensions so we can fill the flex container exactly
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
      setContainerHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const track = (e: MouseEvent) => { mouseYRef.current = e.clientY; };
    window.addEventListener('mousemove', track);
    return () => window.removeEventListener('mousemove', track);
  }, []);

  useEffect(() => {
    if (!isSelecting) { if (rafRef.current) cancelAnimationFrame(rafRef.current); return; }
    const BUFFER = 60, MAX_SPD = 18;
    const tick = () => {
      if (gridRef.current) {
        const { top, bottom } = gridRef.current.getBoundingClientRect();
        const y = mouseYRef.current;
        if (y > bottom - BUFFER) gridRef.current.scrollTop += Math.min(MAX_SPD, ((y - (bottom - BUFFER)) / BUFFER) * MAX_SPD);
        else if (y < top + BUFFER) gridRef.current.scrollTop -= Math.min(MAX_SPD, (((top + BUFFER) - y) / BUFFER) * MAX_SPD);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isSelecting]);

  const shiftOptions = getShiftOptions(dutyType);

  const daysInMonth = useMemo(() => {
    const year = currentMonth.getFullYear(), month = currentMonth.getMonth();
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
      const d  = new Date(date);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      days.push({
        full: `${d.getFullYear()}-${mm}-${dd}`,
        dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: d.getDate(),
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
      });
      date.setDate(date.getDate() + 1);
    }
    return days;
  }, [currentMonth]);

  const shiftHasAssistant = useMemo(() =>
    shifts.map(s => daysInMonth.some(d => !!(scheduleMap[d.full]?.[s.type]?.hasAssistant && scheduleMap[d.full]?.[s.type]?.assistant))),
  [shifts, daysInMonth, scheduleMap]);

  /** Days this month where the column has a nurse on it (an assistant counts). */
  const assignedDaysIn = useCallback((type: string) => daysInMonth.filter(d => {
    const a = scheduleMap[d.full]?.[type];
    return !!a && !a.is_no_duty && !!(a.nurse || (a.hasAssistant && a.assistant));
  }).length, [daysInMonth, scheduleMap]);

  /** Days this month the column holds anything at all, NO DUTY markers included. */
  const filledDaysIn = useCallback((type: string) =>
    daysInMonth.filter(d => !!scheduleMap[d.full]?.[type]).length,
  [daysInMonth, scheduleMap]);

  const monthLabel = currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  /**
   * What is under a column, in words, for the confirmation.
   *
   * Staffed days and NO DUTY markers are counted apart: both are deleted, but
   * only one of them is somebody's roster and pay, and a warning that said
   * "31 entries" for a month of NO DUTY would train people to click through.
   */
  const describeColumnContents = (type: string) => {
    const staffed = assignedDaysIn(type);
    const marked = filledDaysIn(type) - staffed;
    const parts: string[] = [];
    if (staffed) parts.push(`${staffed} day${staffed === 1 ? '' : 's'} with a nurse assigned`);
    if (marked) parts.push(`${marked} NO DUTY day${marked === 1 ? '' : 's'}`);
    return parts.join(' and ');
  };

  const syncPreferencesToDB = async (updated: ShiftColumn[]) => {
    if (!patientId) return;
    try {
      const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('accessToken');
      await axios.post(`${base}/api/patients/${patientId}/shift-preferences`, { shift_order: updated }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) { console.error('Failed to save column preferences', err); }
  };

  const handleDragStart = (e: React.DragEvent, idx: number) => { setDraggedColIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    if (draggedColIdx === null || draggedColIdx === targetIdx) return;
    const next = [...shifts];
    const [item] = next.splice(draggedColIdx, 1);
    next.splice(targetIdx, 0, item);
    setShifts(next); setDraggedColIdx(null); syncPreferencesToDB(next);
  };

  const addShiftColumn = () => {
    if (shifts.length >= 7) return;
    const next = [...shifts, { id: `shift_${Date.now()}`, label: `Shift ${shifts.length + 1}`, type: 'Select Shift...' }];
    setShifts(next); syncPreferencesToDB(next);
  };
  /**
   * The label a shift type would actually take on this column: the plain name
   * when no other column holds it, otherwise the first free "- 2", "- 3", …
   *
   * Shared by the dropdown and the rename below, so the option someone picks is
   * the label they get. The dropdown used to offer "08 Morning" when a column
   * already had it and then silently save "08 Morning - 2", which read as the
   * grid ignoring the choice.
   */
  const nextFreeType = useCallback((base: string, selfId: string) => {
    if (base === PLACEHOLDER) return base;
    // The column being named is excluded, so it can always keep the type it
    // already has instead of bumping itself to "- 2".
    const taken = new Set(shifts.filter(sh => sh.id !== selfId).map(sh => sh.type));
    if (!taken.has(base)) return base;
    let ctr = 2;
    while (taken.has(`${base} - ${ctr}`)) ctr++;
    return `${base} - ${ctr}`;
  }, [shifts]);

  const removeColumn = (id: string) => {
    const next = shifts.filter(sh => sh.id !== id);
    setShifts(next); syncPreferencesToDB(next);
  };

  /**
   * Drop a column, taking this month's assignments under it with it.
   *
   * Deleting a column used to remove it from the grid and leave every
   * assignment in place: the nurse stayed rostered and on the salary sheet, and
   * came back the moment the column was re-added. A column with anything under
   * it now asks first, and the confirmation says how much goes.
   *
   * Only the month on screen is cleared — see delete_shift_range on the server.
   */
  const handleDeleteColumn = (col: ShiftColumn) => {
    if (assignedDaysIn(col.type) === 0 && filledDaysIn(col.type) === 0) {
      removeColumn(col.id);           // nothing under it, nothing to warn about
      return;
    }
    setPendingColumnDelete(col);
  };

  const confirmDeleteColumn = async () => {
    const col = pendingColumnDelete;
    setPendingColumnDelete(null);
    if (!col) return;
    // The column goes either way; only the assignments need the server. Dropping
    // it first keeps the grid responsive, and a failure is reported rather than
    // rolled back — the rows are still there to try again on.
    removeColumn(col.id);
    if (!patientId) return;
    try {
      const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('accessToken');
      await axios.post(`${base}/api/schedule/delete-shift-range`, {
        patient_id: patientId,
        shift_type: col.type,
        start_date: daysInMonth[0].full,
        end_date: daysInMonth[daysInMonth.length - 1].full,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onAssignmentsDeleted?.();
    } catch (err) {
      console.error('Failed to delete the shift column’s assignments', err);
      window.alert(
        `The column was removed, but its assignments could not be deleted.\n\n`
        + `Add "${col.type}" back and try again, or they will stay on the salary sheet.`,
      );
    }
  };

  const handleRenameColumn = (id: string, newBase: string) => {
    const final = nextFreeType(newBase, id);
    const next = shifts.map(sh => sh.id === id ? { ...sh, type: final } : sh);
    setShifts(next); syncPreferencesToDB(next);
  };

  const startSelection = (dayIdx: number, shiftIdx: number) => {
    if (shifts[shiftIdx].type === 'Select Shift...') return;
    setSelectedSlots([]); // clear previous selection; drawer closes, drag starts clean
    setIsSelecting(true);
    setSelectionStart({ dayIdx, shiftIdx });
    setSelectionEnd({ dayIdx, shiftIdx });
    // selectedSlots is intentionally NOT set here — finishSelection sets it on mouseUp
  };
  const updateSelection = (dayIdx: number, shiftIdx: number) => { if (isSelecting) setSelectionEnd({ dayIdx, shiftIdx }); };
  const finishSelection = useCallback(() => {
    if (!isSelecting || !selectionStart || !selectionEnd) return;
    const minD = Math.min(selectionStart.dayIdx, selectionEnd.dayIdx);
    const maxD = Math.max(selectionStart.dayIdx, selectionEnd.dayIdx);
    const minS = Math.min(selectionStart.shiftIdx, selectionEnd.shiftIdx);
    const maxS = Math.max(selectionStart.shiftIdx, selectionEnd.shiftIdx);
    const slots: SelectedSlot[] = [];
    for (let d = minD; d <= maxD; d++)
      for (let s = minS; s <= maxS; s++)
        if (shifts[s].type !== 'Select Shift...') slots.push({ date: daysInMonth[d].full, shiftId: shifts[s].id, session: shifts[s].type });
    setSelectedSlots(slots);
    setIsSelecting(false);
  }, [isSelecting, selectionStart, selectionEnd, daysInMonth, shifts, setSelectedSlots]);

  useEffect(() => {
    const up = () => { if (isSelecting) finishSelection(); };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [isSelecting, finishSelection]);

  const isCellInDragSelection = (dayIdx: number, shiftIdx: number) => {
    if (!isSelecting || !selectionStart || !selectionEnd) return false;
    const minD = Math.min(selectionStart.dayIdx, selectionEnd.dayIdx);
    const maxD = Math.max(selectionStart.dayIdx, selectionEnd.dayIdx);
    const minS = Math.min(selectionStart.shiftIdx, selectionEnd.shiftIdx);
    const maxS = Math.max(selectionStart.shiftIdx, selectionEnd.shiftIdx);
    return dayIdx >= minD && dayIdx <= maxD && shiftIdx >= minS && shiftIdx <= maxS;
  };
  const isCellSelected = (date: string, shiftId: string) => selectedSlots.some(sl => sl.date === date && sl.shiftId === shiftId);

  // ---- Column widths ----
  // 5 cols per shift: [Date | Nurse | SHIFTS | HRS | CHARGE]
  // Multiple shifts: fill container width exactly (3 visible, 4+ scroll)
  // Single shift: capped + centered
  const ADD_BTN_W   = shifts.length < 7 ? 40 : 0;
  const DATE_W      = 62;
  const SHIFTS_W    = 52;
  const HRS_W       = 50;
  const CHARGE_W    = 72;
  const FIXED_COLS  = DATE_W + SHIFTS_W + HRS_W + CHARGE_W; // 236

  // Always size columns as if at most 2 are visible — 3+ shifts scroll horizontally at the same column size
  const nurseW = Math.min(
    Math.max(Math.floor((containerWidth - ADD_BTN_W) / Math.min(Math.max(shifts.length, 1), 2) - FIXED_COLS), 120),
    150
  );

  // When a shift has an assistant, use dedicated wider widths for both columns
  const PRIMARY_NURSE_W = 130;
  const ASST_W          = 110;

  const tableWidth = shifts.reduce((acc, _, idx) =>
    acc + DATE_W + (shiftHasAssistant[idx] ? PRIMARY_NURSE_W + ASST_W : nurseW) + SHIFTS_W + HRS_W + CHARGE_W,
    ADD_BTN_W
  );

  // Center only when the table actually fits — avoids bilateral overflow with margin:auto on wider tables
  const isCentered = tableWidth <= containerWidth;

  // ---- Row height: distribute available vertical space across all days ----
  // containerHeight is measured by ResizeObserver; FIXED_H accounts for the two header rows + footer
  const DATA_ROWS = daysInMonth.length; // 28–31
  const ROW_H     = Math.max(Math.floor((containerHeight - FIXED_H) / DATA_ROWS), 16);

  const GRID_BORDER = '1px solid #e2e8f0';
  const today    = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const shiftTotals = useMemo(() => shifts.map(s => {
    let totalShifts = 0, totalHrs = 0, totalCharge = 0;
    daysInMonth.forEach(d => {
      const data = scheduleMap[d.full]?.[s.type];
      if (data && !data.is_no_duty) {
        totalShifts += data.hasAssistant ? 2 : 1;
        totalHrs    += calcHrs(data.in_time || '', data.out_time || '') || data.actual_hours || 0;
        totalCharge += cellCharge(data);
      }
    });
    return { totalShifts, totalHrs: Math.round(totalHrs * 10) / 10, totalCharge };
  }), [shifts, scheduleMap, daysInMonth]);

  return (
    <div
      ref={gridRef}
      className="sg-scroll"
      style={{
        flex: 1, minHeight: 0,
        overflowX: 'auto',
        overflowY: 'hidden', // no vertical scroll; table fills exactly
        backgroundColor: '#f8fafc',
      }}
    >
      <style>{`
        .sg-scroll::-webkit-scrollbar { width: 0; height: 8px; }
        .sg-scroll::-webkit-scrollbar-track { background: #e2e8f0; }
        .sg-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .sg-scroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
        .drag-active { opacity: 0.3 !important; }
        .sg-row:hover td { background-color: #f8fafc !important; }
        /* Selected cells keep their bright highlight — even on row hover. */
        td.sg-sel, .sg-row:hover td.sg-sel { background-color: #facc15 !important; }
      `}</style>

      <table style={{
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        width: `${tableWidth}px`,
        // Center when single shift column
        margin: isCentered ? '0 auto' : undefined,
        // Fill the full container height so no blank area remains
        height: `${containerHeight}px`,
      }}>
        <colgroup>
          {shifts.map((s, idx) => (
            <React.Fragment key={s.id}>
              <col style={{ width: `${DATE_W}px` }} />
              {shiftHasAssistant[idx] ? (
                <>
                  <col style={{ width: `${PRIMARY_NURSE_W}px` }} />
                  <col style={{ width: `${ASST_W}px` }} />
                </>
              ) : (
                <col style={{ width: `${nurseW}px` }} />
              )}
              <col style={{ width: `${SHIFTS_W}px` }} />
              <col style={{ width: `${HRS_W}px` }} />
              <col style={{ width: `${CHARGE_W}px` }} />
            </React.Fragment>
          ))}
          {shifts.length < 7 && <col style={{ width: `${ADD_BTN_W}px` }} />}
        </colgroup>

        <thead>
          {/* Row 1: shift type header */}
          <tr style={{ height: `${H1}px` }}>
            {shifts.map((s, idx) => {
              const ct = SESSION_COLORS[idx % SESSION_COLORS.length];
              const isBlank = s.type === PLACEHOLDER;
              // What each shift type would be called on THIS column. A type
              // another column already holds appears as "08 Morning - 2" rather
              // than as a second "08 Morning" that silently renames itself.
              const colOptions = shiftOptions.map((opt, i) =>
                i === 0 ? opt : nextFreeType(opt, s.id));
              return (
                <th key={s.id} colSpan={shiftHasAssistant[idx] ? 6 : 5} draggable
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragOver={handleDragOver}
                  onDrop={e => handleDrop(e, idx)}
                  className={draggedColIdx === idx ? 'drag-active' : ''}
                  style={{
                    padding: '0 10px', height: `${H1}px`, border: GRID_BORDER, cursor: 'grab',
                    backgroundColor: isBlank ? '#f8fafc' : ct.headerBg,
                    borderTop: `3px solid ${isBlank ? '#e2e8f0' : ct.dot}`,
                    position: 'sticky', top: 0, zIndex: 20,
                    fontSize: '0.72rem', fontWeight: 700, color: '#374151',
                    textAlign: 'left', whiteSpace: 'nowrap',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                      <GripHorizontal size={12} color="#94a3b8" style={{ flexShrink: 0 }} />
                      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                        <select
                          style={{ width: '100%', border: 'none', background: 'transparent', fontWeight: 700, fontSize: '0.75rem', color: isBlank ? '#94a3b8' : ct.dot, outline: 'none', cursor: 'pointer', appearance: 'none', paddingRight: '16px' }}
                          value={s.type} onChange={e => handleRenameColumn(s.id, e.target.value)}
                        >
                          {colOptions.map((opt, i) => <option key={opt} value={opt} disabled={i === 0}>{opt}</option>)}
                          {/* A label the list can't produce — an older or
                              hand-made one — so the select still shows what the
                              column is actually set to. */}
                          {s.type !== PLACEHOLDER && !colOptions.includes(s.type) && <option value={s.type}>{s.type}</option>}
                        </select>
                        <ChevronDown size={11} color="#94a3b8" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                      </div>
                    </div>
                    {shifts.length > 1 && (
                      <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => { e.stopPropagation(); handleDeleteColumn(s); }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', marginLeft: '4px' }}
                        title="Remove"
                      >
                        <Trash2 size={12} color="#f87171" />
                      </button>
                    )}
                  </div>
                </th>
              );
            })}
            {shifts.length < 7 && (
              <th rowSpan={2} style={{
                width: `${ADD_BTN_W}px`, border: GRID_BORDER, textAlign: 'center', verticalAlign: 'middle',
                backgroundColor: '#f8fafc', position: 'sticky', top: 0, zIndex: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}>
                <button
                  onClick={addShiftColumn}
                  title="Add shift column"
                  style={{
                    width: '26px', height: '26px', borderRadius: '50%',
                    backgroundColor: '#2563eb', color: 'white',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto',
                    boxShadow: '0 2px 6px rgba(37,99,235,0.35)',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 10px rgba(37,99,235,0.45)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 6px rgba(37,99,235,0.35)'; }}
                >
                  <Plus size={14} strokeWidth={2.5} />
                </button>
              </th>
            )}
          </tr>

          {/* Row 2: sub-column labels */}
          <tr style={{ height: `${H2}px` }}>
            {shifts.map((s, idx) => {
              const ct = SESSION_COLORS[idx % SESSION_COLORS.length];
              return (
                <React.Fragment key={s.id}>
                  {[
                    { label: 'DATE',   w: DATE_W,                                               sep: false },
                    { label: 'NURSE',  w: shiftHasAssistant[idx] ? PRIMARY_NURSE_W : nurseW,   sep: false },
                    ...(shiftHasAssistant[idx] ? [{ label: 'ASST', w: ASST_W, sep: false }] : []),
                    { label: 'SHIFTS', w: SHIFTS_W, sep: true  },
                    { label: 'HRS',    w: HRS_W,    sep: true  },
                    { label: 'CHARGE', w: CHARGE_W, sep: true  },
                  ].map((col, ci) => (
                    <th key={ci} style={{
                      border: GRID_BORDER, padding: '1px 3px', height: `${H2}px`,
                      backgroundColor: ct.bg, position: 'sticky', top: `${H1}px`, zIndex: 19,
                      fontSize: '0.57rem', fontWeight: 700, color: ct.text,
                      textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center',
                      borderTop: `2px solid ${ct.border}`,
                      width: `${col.w}px`, minWidth: `${col.w}px`,
                      ...(col.sep ? { borderLeft: `2px solid ${ct.border}` } : {}),
                    }}>
                      {col.label}
                    </th>
                  ))}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {isFetching ? (
            <tr><td colSpan={shifts.reduce((acc, _, i) => acc + (shiftHasAssistant[i] ? 6 : 5), 0) + 1} style={{ textAlign: 'center', padding: '3rem', color: '#64748b', fontWeight: 600 }}>Loading schedule...</td></tr>
          ) : daysInMonth.map((d, dIdx) => {
            const isToday   = d.full === todayStr;
            const dateBg    = isToday ? '#1d4ed8' : d.isWeekend ? '#bbf7d0' : '#f1f5f9';
            const dateClr   = isToday ? 'white'   : d.isWeekend ? '#15803d' : '#374151';
            const dayClr    = isToday ? 'rgba(255,255,255,0.65)' : d.isWeekend ? '#4ade80' : '#94a3b8';

            return (
              <tr key={d.full} className="sg-row" style={{ height: `${ROW_H}px`, backgroundColor: 'white' }}>
                {shifts.map((s, sIdx) => {
                  const existingData  = scheduleMap[d.full]?.[s.type] ?? null;
                  const isPlaceholder = s.type === 'Select Shift...';
                  const ct            = SESSION_COLORS[sIdx % SESSION_COLORS.length];
                  const filled        = !!(existingData && !existingData.is_no_duty);
                  const isEdited      = !!(existingData?.is_overridden);
                  const hrs           = filled ? (calcHrs(existingData!.in_time || '', existingData!.out_time || '') || existingData!.actual_hours || 0) : 0;
                  const isSelected    = isCellInDragSelection(dIdx, sIdx) || isCellSelected(d.full, s.id);
                  // A "change alert" flag on this cell (assignment changed today, or flagged on the board).
                  const isFlagged     = !isPlaceholder && !!flaggedCells?.has(`${d.full}|${s.type}`);

                  const selBg  = isSelected ? '#facc15' : undefined;
                  const cellBg = selBg || (isFlagged ? '#fde68a' : (filled ? (isEdited ? '#fef9c3' : 'white') : '#fff0f0'));
                  const dtBg   = selBg || (isToday ? '#1d4ed8' : isFlagged ? '#fcd34d' : filled ? (isEdited ? '#fef08a' : dateBg) : '#ffe4e4');
                  const dtClr  = selBg ? '#713f12' : isToday ? 'white' : isFlagged ? '#713f12' : filled ? (isEdited ? '#92400e' : dateClr) : '#dc2626';
                  const dtDClr = selBg ? '#92400e' : isToday ? 'rgba(255,255,255,0.7)' : isFlagged ? '#92400e' : filled ? (isEdited ? '#b45309' : dayClr) : '#fca5a5';
                  // class marks selected cells so the row-hover style can't wash them out
                  const cls = `${draggedColIdx === sIdx ? 'drag-active ' : ''}${isSelected ? 'sg-sel' : ''}`.trim();

                  const fs = Math.min(Math.max(ROW_H * 0.38, 8), 12);

                  const base: React.CSSProperties = {
                    border: GRID_BORDER, padding: '0 4px',
                    fontSize: `${fs}px`, verticalAlign: 'middle',
                    userSelect: 'none', cursor: isPlaceholder ? 'not-allowed' : 'crosshair',
                    opacity: isPlaceholder ? 0.4 : 1, overflow: 'hidden',
                  };
                  const mh = {
                    onMouseDown: () => startSelection(dIdx, sIdx),
                    onMouseEnter: () => updateSelection(dIdx, sIdx),
                  };

                  return (
                    <React.Fragment key={s.id}>
                      {/* Date */}
                      <td {...mh} className={cls}
                        style={{ ...base, backgroundColor: dtBg, textAlign: 'center', padding: '0 2px' }}
                      >
                        <div style={{ fontWeight: 800, lineHeight: 1, color: dtClr, fontSize: `${Math.min(fs + 1, 13)}px` }}>{d.dayNum}</div>
                        <div style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: dtDClr, fontSize: `${Math.max(fs - 2, 7)}px`, lineHeight: 1 }}>{d.dayName}</div>
                      </td>

                      {/* Nurse */}
                      <td {...mh} className={cls}
                        style={{ ...base, backgroundColor: cellBg, textAlign: 'center', borderLeft: `3px solid ${isFlagged ? '#d97706' : filled ? (isEdited ? '#f59e0b' : ct.dot) : '#fca5a5'}` }}
                        onMouseMove={filled && existingData?.nurse ? (e) => {
                          setStaffTooltip({
                            role: 'primary',
                            name: existingData!.nurse,
                            shiftType: s.type,
                            inTime: existingData!.in_time,
                            outTime: existingData!.out_time,
                            hours: hrs || undefined,
                            charge: existingData!.total_charge || undefined,
                            x: e.clientX, y: e.clientY,
                          });
                        } : undefined}
                        onMouseLeave={() => setStaffTooltip(null)}
                      >
                        {filled ? (
                          <div style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                            {existingData!.nurse}
                          </div>
                        ) : isSelected ? (
                          <CheckCircle2 size={Math.min(fs + 2, 14)} color="#3b82f6" />
                        ) : (
                          <span style={{ fontWeight: 700, color: '#f87171', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: `${Math.max(fs - 1, 7)}px` }}>No Duty</span>
                        )}
                      </td>

                      {/* Assistant — only rendered when this shift has any assistant on any day */}
                      {shiftHasAssistant[sIdx] && (
                        <td {...mh} className={cls}
                          style={{ ...base, backgroundColor: cellBg, textAlign: 'left', borderLeft: `1px dashed #c4b5fd` }}
                          onMouseMove={filled && existingData?.hasAssistant && existingData?.assistant ? (e) => {
                            const aHrs = existingData!.second_in_time && existingData!.second_out_time
                              ? calcHrs(existingData!.second_in_time, existingData!.second_out_time)
                              : calcHrs(existingData!.in_time || '', existingData!.out_time || '');
                            setStaffTooltip({
                              role: 'assistant',
                              name: existingData!.assistant!,
                              shiftType: s.type,
                              inTime: existingData!.second_in_time || existingData!.in_time,
                              outTime: existingData!.second_out_time || existingData!.out_time,
                              hours: aHrs || undefined,
                              charge: existingData!.second_total_charge || undefined,
                              x: e.clientX, y: e.clientY,
                            });
                          } : undefined}
                          onMouseLeave={() => setStaffTooltip(null)}
                        >
                          {filled && existingData?.hasAssistant && existingData?.assistant ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '2px', fontWeight: 600, color: '#7c3aed', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <UserPlus size={Math.max(fs - 2, 7)} style={{ flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{existingData.assistant}</span>
                            </div>
                          ) : null}
                        </td>
                      )}

                      {/* Shifts */}
                      <td {...mh} className={cls}
                        style={{ ...base, backgroundColor: cellBg, textAlign: 'center', fontWeight: 600, color: '#374151', borderLeft: `2px solid ${ct.border}` }}
                      >
                        {filled ? (existingData!.hasAssistant ? 2 : 1) : ''}
                      </td>

                      {/* Hrs */}
                      <td {...mh} className={cls}
                        style={{ ...base, backgroundColor: cellBg, textAlign: 'center', fontWeight: 600, color: '#374151', borderLeft: `2px solid ${ct.border}` }}
                      >
                        {hrs > 0 ? `${hrs}h` : ''}
                      </td>

                      {/* Charge */}
                      <td {...mh} className={cls}
                        style={{ ...base, backgroundColor: cellBg, textAlign: 'center', fontWeight: 600, color: '#15803d', borderLeft: `2px solid ${ct.border}` }}
                      >
                        {filled && cellCharge(existingData!) ? `₹${cellCharge(existingData!)}` : ''}
                      </td>
                    </React.Fragment>
                  );
                })}
                {shifts.length < 7 && <td style={{ border: GRID_BORDER, backgroundColor: '#f8fafc', cursor: 'default' }} />}
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr style={{ height: `${HF}px` }}>
            {shifts.map((s, idx) => {
              const ct  = SESSION_COLORS[idx % SESSION_COLORS.length];
              const tot = shiftTotals[idx];
              const fb: React.CSSProperties = {
                border: GRID_BORDER, padding: '3px 5px', fontWeight: 700,
                fontSize: '0.7rem', backgroundColor: ct.bg, color: ct.text,
                position: 'sticky', bottom: 0, zIndex: 8,
                borderTop: `2px solid ${ct.border}`, height: `${HF}px`,
              };
              return (
                <React.Fragment key={s.id}>
                  <td style={{ ...fb, textAlign: 'center', fontSize: '0.57rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</td>
                  <td style={{ ...fb }} />
                  {shiftHasAssistant[idx] && <td style={{ ...fb }} />}
                  <td style={{ ...fb, textAlign: 'center', borderLeft: `2px solid ${ct.border}` }}>{tot.totalShifts || ''}</td>
                  <td style={{ ...fb, textAlign: 'center', borderLeft: `2px solid ${ct.border}` }}>{tot.totalHrs > 0 ? `${tot.totalHrs}h` : ''}</td>
                  <td style={{ ...fb, textAlign: 'center', color: '#15803d', borderLeft: `2px solid ${ct.border}` }}>{tot.totalCharge > 0 ? `₹${tot.totalCharge}` : ''}</td>
                </React.Fragment>
              );
            })}
            {shifts.length < 7 && <td style={{ border: GRID_BORDER, backgroundColor: '#f8fafc', position: 'sticky', bottom: 0 }} />}
          </tr>
        </tfoot>
      </table>
      {staffTooltip && (
        <div style={{
          position: 'fixed', zIndex: 9999, pointerEvents: 'none',
          background: '#1e293b', color: 'white', borderRadius: '0.75rem',
          padding: '10px 14px', fontSize: '0.78rem',
          minWidth: '180px', maxWidth: '220px',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
          border: '1px solid #334155',
          left: Math.min(staffTooltip.x + 14, window.innerWidth - 240),
          top: staffTooltip.y - 10,
        }}>
          {/* Accent matches the cell the name is read from: violet assistant, blue primary. */}
          <div style={{ fontWeight: 900, fontSize: '0.82rem', marginBottom: '6px', color: staffTooltip.role === 'assistant' ? '#c4b5fd' : '#93c5fd' }}>
            {staffTooltip.name}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: '#94a3b8', marginBottom: '2px', fontSize: '0.72rem' }}>
            <span>Shift</span>
            <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{staffTooltip.shiftType}</span>
          </div>
          {staffTooltip.inTime && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: '#94a3b8', marginBottom: '2px', fontSize: '0.72rem' }}>
              <span>Time</span>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{staffTooltip.inTime} – {staffTooltip.outTime}</span>
            </div>
          )}
          {staffTooltip.hours ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: '#94a3b8', marginBottom: '2px', fontSize: '0.72rem' }}>
              <span>Hours</span>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{staffTooltip.hours}h</span>
            </div>
          ) : null}
          {staffTooltip.charge ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: '#94a3b8', fontSize: '0.72rem' }}>
              <span>Charge</span>
              <span style={{ color: '#86efac', fontWeight: 600 }}>₹{staffTooltip.charge}</span>
            </div>
          ) : null}
          <div style={{ fontSize: '0.62rem', color: '#475569', marginTop: '6px', borderTop: '1px solid #334155', paddingTop: '6px' }}>
            {staffTooltip.role === 'assistant' ? 'Assistant Nurse' : 'Primary Nurse'}
          </div>
        </div>
      )}

      {/* Removing a column takes this month's assignments under it with it, so
          it asks first and says how many. Silently orphaning them was the old
          behaviour: the column vanished and the nurse stayed rostered. */}
      <ConfirmDialog
        open={pendingColumnDelete !== null}
        danger
        title={pendingColumnDelete
          ? `Delete the ${pendingColumnDelete.type} column?`
          : ''}
        message={pendingColumnDelete
          ? `It has ${describeColumnContents(pendingColumnDelete.type)} in ${monthLabel}.`
          : ''}
        points={pendingColumnDelete ? [
          `Every ${pendingColumnDelete.type} entry in ${monthLabel} is deleted, for this case — including hours and charges on the salary sheet.`,
          'Other months keep their records; only the month on screen is cleared.',
          'This cannot be undone. The nurses stay on the roster for their other shifts.',
        ] : []}
        confirmLabel="Delete column and assignments"
        cancelLabel="Keep it"
        onConfirm={confirmDeleteColumn}
        onCancel={() => setPendingColumnDelete(null)}
      />
    </div>
  );
};

export default ScheduleGrid;
